import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const authHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
  return { headers: { Authorization: `Bearer ${token}` } };
};

// ─────────────────────────────────────────────────────────────
// Chart of Accounts tab
// ─────────────────────────────────────────────────────────────
function ChartOfAccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/accounting/accounts?include_totals=true`, authHeaders());
      setAccounts(data);
    } catch (e) { toast.error("Failed to load accounts"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = { asset: [], liability: [], equity: [], revenue: [], expense: [] };
    for (const a of accounts) g[a.type]?.push(a);
    return g;
  }, [accounts]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Chart of Accounts</h2>
          <p className="text-sm text-neutral-500">{accounts.length} accounts · UK Ltd standard seed</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="reload-coa-btn">Reload</Button>
      </div>
      {loading ? <div>Loading…</div> : Object.entries(grouped).map(([type, rows]) => rows.length > 0 && (
        <Card key={type} data-testid={`coa-group-${type}`}>
          <CardHeader><CardTitle className="capitalize text-base">{type} ({rows.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-[80px]">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>VAT</TableHead>
                <TableHead className="text-right">DR total</TableHead>
                <TableHead className="text-right">CR total</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(a => (
                  <TableRow key={a.code} data-testid={`coa-row-${a.code}`}>
                    <TableCell className="font-mono">{a.code}</TableCell>
                    <TableCell>{a.name}{a.is_bank && <Badge variant="secondary" className="ml-2">bank</Badge>}</TableCell>
                    <TableCell><Badge variant="outline">{a.vat_treatment}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{gbp(a.debit_total)}</TableCell>
                    <TableCell className="text-right font-mono">{gbp(a.credit_total)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{gbp(a.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Journals tab — list + create dialog
// ─────────────────────────────────────────────────────────────
function JournalsTab() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const [j, a] = await Promise.all([
        axios.get(`${API}/api/accounting/journals?limit=100`, authHeaders()),
        axios.get(`${API}/api/accounting/accounts?active_only=true`, authHeaders()),
      ]);
      setEntries(j.data);
      setAccounts(a.data);
    } catch (e) { toast.error("Failed to load journals"); }
  };
  useEffect(() => { load(); }, []);

  const reverse = async (id) => {
    if (!window.confirm("Post a reversal entry? This creates a mirror journal for audit trail.")) return;
    try {
      await axios.post(`${API}/api/accounting/journals/${id}/reverse`, {}, authHeaders());
      toast.success("Reversed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reversal failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Journal Entries</h2>
          <p className="text-sm text-neutral-500">{entries.length} recent posted entries</p>
        </div>
        <div className="flex gap-2">
          <ImportXeroDialog onSaved={load} />
          <PayrollDialog onSaved={load} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild><Button data-testid="new-journal-btn">New Journal Entry</Button></DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>New Manual Journal</DialogTitle></DialogHeader>
              <NewJournalForm accounts={accounts} onSaved={() => { setDialogOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Entry #</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="text-right">Total DR</TableHead>
            <TableHead className="text-right">Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {entries.map(e => (
              <TableRow key={e.id} data-testid={`journal-row-${e.entry_number}`}>
                <TableCell className="font-mono">{e.entry_number}</TableCell>
                <TableCell>{(e.entry_date || "").slice(0, 10)}</TableCell>
                <TableCell className="max-w-md truncate">{e.description}</TableCell>
                <TableCell><Badge variant="outline">{e.source}</Badge></TableCell>
                <TableCell className="text-right font-mono">{gbp(e.totals?.debit)}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={e.status === "posted" ? "default" : "secondary"}>{e.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {e.status === "posted" && !e.reversed_by_entry_id && (
                    <Button size="sm" variant="ghost" onClick={() => reverse(e.id)}>Reverse</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

function NewJournalForm({ accounts, onSaved }) {
  const [description, setDescription] = useState("");
  const [entryDate, setEntryDate] = useState(today());
  const [lines, setLines] = useState([
    { account_code: "", debit_gbp: 0, credit_gbp: 0, description: "" },
    { account_code: "", debit_gbp: 0, credit_gbp: 0, description: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const totalDr = lines.reduce((s, l) => s + Number(l.debit_gbp || 0), 0);
  const totalCr = lines.reduce((s, l) => s + Number(l.credit_gbp || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.005 && totalDr > 0;

  const addLine = () => setLines([...lines, { account_code: "", debit_gbp: 0, credit_gbp: 0, description: "" }]);
  const setLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!balanced) { toast.error("Journal is not balanced"); return; }
    if (!description.trim()) { toast.error("Description required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/accounting/journals`, {
        description,
        entry_date: entryDate,
        lines: lines.map(l => ({ ...l, debit_gbp: Number(l.debit_gbp || 0), credit_gbp: Number(l.credit_gbp || 0) })),
      }, authHeaders());
      toast.success("Journal posted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><Label>Description</Label><Input value={description} onChange={e => setDescription(e.target.value)} data-testid="journal-desc-input" /></div>
        <div><Label>Date</Label><Input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} /></div>
      </div>
      <div className="border rounded-md">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[220px]">Account</TableHead>
            <TableHead>Line description</TableHead>
            <TableHead className="w-[110px] text-right">Debit</TableHead>
            <TableHead className="w-[110px] text-right">Credit</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {lines.map((ln, i) => (
              <TableRow key={i} data-testid={`journal-line-${i}`}>
                <TableCell>
                  <Select value={ln.account_code} onValueChange={v => setLine(i, { account_code: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick account" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Input value={ln.description} onChange={e => setLine(i, { description: e.target.value })} /></TableCell>
                <TableCell><Input type="number" step="0.01" className="text-right" value={ln.debit_gbp}
                  onChange={e => setLine(i, { debit_gbp: e.target.value, credit_gbp: 0 })} /></TableCell>
                <TableCell><Input type="number" step="0.01" className="text-right" value={ln.credit_gbp}
                  onChange={e => setLine(i, { credit_gbp: e.target.value, debit_gbp: 0 })} /></TableCell>
                <TableCell>{lines.length > 2 && <Button variant="ghost" size="sm" onClick={() => rmLine(i)}>×</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between items-center">
        <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">+ Add line</Button>
        <div className="font-mono text-sm flex gap-6">
          <span>Total DR: <b>{gbp(totalDr)}</b></span>
          <span>Total CR: <b>{gbp(totalCr)}</b></span>
          <span className={balanced ? "text-green-600" : "text-red-600"}>{balanced ? "Balanced ✓" : "Not balanced"}</span>
        </div>
      </div>
      <div className="flex justify-end"><Button onClick={save} disabled={!balanced || saving} data-testid="post-journal-btn">
        {saving ? "Posting…" : "Post journal"}
      </Button></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Reports tab
// ─────────────────────────────────────────────────────────────
function ReportsTab() {
  const [tab, setTab] = useState("trial-balance");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [basis, setBasis] = useState("accrual");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setReport(null);
    try {
      let url = "";
      if (tab === "trial-balance") url = `${API}/api/accounting/reports/trial-balance?as_of=${to}`;
      else if (tab === "profit-loss") url = `${API}/api/accounting/reports/profit-loss?period_from=${from}&period_to=${to}&basis=${basis}`;
      else if (tab === "balance-sheet") url = `${API}/api/accounting/reports/balance-sheet?as_of=${to}`;
      else if (tab === "vat-return") url = `${API}/api/accounting/reports/vat-return?period_from=${from}&period_to=${to}`;
      const { data } = await axios.get(url, authHeaders());
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to run report"); }
    setLoading(false);
  };

  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, basis]);

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reports-subtabs">
          <TabsTrigger value="trial-balance" data-testid="rpt-tb-tab">Trial Balance</TabsTrigger>
          <TabsTrigger value="profit-loss" data-testid="rpt-pl-tab">P&amp;L</TabsTrigger>
          <TabsTrigger value="balance-sheet" data-testid="rpt-bs-tab">Balance Sheet</TabsTrigger>
          <TabsTrigger value="vat-return" data-testid="rpt-vat-tab">VAT Return</TabsTrigger>
        </TabsList>

        <div className="flex items-end gap-3 mt-4 flex-wrap">
          {tab !== "trial-balance" && tab !== "balance-sheet" && (
            <div><Label>From</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
          )}
          <div><Label>{tab === "trial-balance" || tab === "balance-sheet" ? "As of" : "To"}</Label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
          {tab === "profit-loss" && (
            <div className="flex flex-col gap-1">
              <Label>Basis</Label>
              <div className="flex gap-1 border rounded-md p-1">
                <button
                  type="button"
                  onClick={() => setBasis("accrual")}
                  className={`px-3 py-1 text-sm rounded ${basis === "accrual" ? "bg-neutral-900 text-white" : "text-neutral-600"}`}
                  data-testid="basis-accrual-btn"
                >Accrual</button>
                <button
                  type="button"
                  onClick={() => setBasis("cash")}
                  className={`px-3 py-1 text-sm rounded ${basis === "cash" ? "bg-neutral-900 text-white" : "text-neutral-600"}`}
                  data-testid="basis-cash-btn"
                >Cash</button>
              </div>
            </div>
          )}
          <Button onClick={run} disabled={loading} data-testid="run-report-btn">{loading ? "Running…" : "Run"}</Button>
        </div>

        <div className="mt-6">
          {report && tab === "trial-balance" && <TrialBalanceView r={report} />}
          {report && tab === "profit-loss"   && (report.basis === "cash" ? <ProfitLossCashView r={report} /> : <ProfitLossView r={report} />)}
          {report && tab === "balance-sheet" && <BalanceSheetView r={report} />}
          {report && tab === "vat-return"    && <VatReturnView r={report} />}
        </div>
      </Tabs>
    </div>
  );
}

const AccTable = ({ rows, netLabel = "Balance" }) => (
  <Table>
    <TableHeader><TableRow>
      <TableHead className="w-[80px]">Code</TableHead>
      <TableHead>Name</TableHead>
      <TableHead className="text-right">Debit</TableHead>
      <TableHead className="text-right">Credit</TableHead>
      <TableHead className="text-right">{netLabel}</TableHead>
    </TableRow></TableHeader>
    <TableBody>
      {rows.map(r => (
        <TableRow key={r.code}>
          <TableCell className="font-mono">{r.code}</TableCell>
          <TableCell>{r.name}</TableCell>
          <TableCell className="text-right font-mono">{gbp(r.debit_total)}</TableCell>
          <TableCell className="text-right font-mono">{gbp(r.credit_total)}</TableCell>
          <TableCell className="text-right font-mono font-semibold">{gbp(r.net)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const TrialBalanceView = ({ r }) => (
  <Card><CardHeader><CardTitle>Trial Balance · as of {(r.as_of || "").slice(0, 10)}</CardTitle></CardHeader>
    <CardContent>
      <AccTable rows={r.accounts} />
      <div className="mt-3 flex justify-end gap-6 font-mono">
        <span>Total DR: <b>{gbp(r.totals.debit)}</b></span>
        <span>Total CR: <b>{gbp(r.totals.credit)}</b></span>
        <span className={r.totals.balanced ? "text-green-600" : "text-red-600"}>
          {r.totals.balanced ? "Balanced ✓" : "OUT OF BALANCE"}
        </span>
      </div>
    </CardContent></Card>
);

const ProfitLossView = ({ r }) => (
  <div className="space-y-4">
    <Card><CardHeader><CardTitle>P&amp;L · {(r.period_from || "").slice(0, 10)} → {(r.period_to || "").slice(0, 10)}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <section><h4 className="font-semibold mb-2">Revenue</h4><AccTable rows={r.revenue} /></section>
        <section><h4 className="font-semibold mb-2">Cost of Sales</h4><AccTable rows={r.cogs} /></section>
        <section><h4 className="font-semibold mb-2">Operating Expenses</h4><AccTable rows={r.opex} /></section>
        <div className="border-t pt-4 space-y-1 font-mono text-right">
          <div>Revenue: <b>{gbp(r.totals.revenue)}</b></div>
          <div>Cost of Sales: {gbp(r.totals.cogs)}</div>
          <div className="text-lg">Gross Profit: <b>{gbp(r.totals.gross_profit)}</b> ({r.totals.gross_margin_pct}%)</div>
          <div>OpEx: {gbp(r.totals.opex)}</div>
          <div className="text-xl">Operating Profit: <b className={r.totals.operating_profit >= 0 ? "text-green-600" : "text-red-600"}>
            {gbp(r.totals.operating_profit)}</b></div>
        </div>
      </CardContent></Card>
  </div>
);

const ProfitLossCashView = ({ r }) => (
  <Card><CardHeader>
    <CardTitle>Cash P&amp;L · {(r.period_from || "").slice(0, 10)} → {(r.period_to || "").slice(0, 10)}</CardTitle>
    <p className="text-xs text-neutral-500 mt-1">{r.note}</p>
  </CardHeader>
    <CardContent className="space-y-4">
      <section><h4 className="font-semibold mb-2">Cash Revenue</h4>
        <Table>
          <TableHeader><TableRow><TableHead>Source</TableHead><TableHead className="text-right">Cash In</TableHead></TableRow></TableHeader>
          <TableBody>{r.revenue.map((ln, i) => (
            <TableRow key={i}><TableCell>{ln.label}</TableCell><TableCell className="text-right font-mono">{gbp(ln.amount)}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </section>
      <section><h4 className="font-semibold mb-2">Cash Expenses</h4>
        <Table>
          <TableHeader><TableRow><TableHead>Source</TableHead><TableHead className="text-right">Cash Out</TableHead></TableRow></TableHeader>
          <TableBody>{r.expenses.map((ln, i) => (
            <TableRow key={i}><TableCell>{ln.label}</TableCell><TableCell className="text-right font-mono">{gbp(ln.amount)}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </section>
      <div className="border-t pt-4 space-y-1 font-mono text-right">
        <div>Cash Revenue: <b>{gbp(r.totals.cash_revenue)}</b></div>
        <div>Cash Expenses: {gbp(r.totals.cash_expenses)}</div>
        <div className="text-xl">Net Cash Flow: <b className={r.totals.net_cash_flow >= 0 ? "text-green-600" : "text-red-600"}>
          {gbp(r.totals.net_cash_flow)}</b> ({r.totals.gross_margin_pct}%)</div>
      </div>
    </CardContent></Card>
);

const BalanceSheetView = ({ r }) => (
  <Card><CardHeader><CardTitle>Balance Sheet · as of {(r.as_of || "").slice(0, 10)}</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <section><h4 className="font-semibold mb-2">Assets ({gbp(r.totals.assets)})</h4><AccTable rows={r.assets} /></section>
      <section><h4 className="font-semibold mb-2">Liabilities ({gbp(r.totals.liabilities)})</h4><AccTable rows={r.liabilities} /></section>
      <section><h4 className="font-semibold mb-2">Equity ({gbp(r.totals.equity)})</h4>
        <AccTable rows={r.equity} />
        <div className="mt-1 text-right font-mono">+ Current Year Earnings: {gbp(r.current_year_earnings)}</div>
      </section>
      <div className="border-t pt-3 font-mono text-right space-y-1">
        <div>Assets: <b>{gbp(r.totals.assets)}</b></div>
        <div>Liabilities + Equity: <b>{gbp(r.totals.liabilities_plus_equity)}</b></div>
        <div className={r.totals.balanced ? "text-green-600" : "text-red-600"}>
          {r.totals.balanced ? "Balanced ✓" : "OUT OF BALANCE"}
        </div>
      </div>
    </CardContent></Card>
);

const VatReturnView = ({ r }) => {
  const b = r.boxes;
  const row = (n, v) => <TableRow><TableCell className="font-mono">{n}</TableCell><TableCell className="text-right font-mono">{gbp(v)}</TableCell></TableRow>;
  return (
    <Card><CardHeader><CardTitle>UK VAT Return · {(r.period_from || "").slice(0, 10)} → {(r.period_to || "").slice(0, 10)}</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Box</TableHead><TableHead className="text-right">Value (£)</TableHead></TableRow></TableHeader>
          <TableBody>
            {row("1 · VAT due on UK sales", b["1_vat_output_uk_sales"])}
            {row("2 · VAT on EC acquisitions", b["2_vat_output_ec_acquisitions"])}
            {row("3 · Total VAT due (1+2)", b["3_total_vat_due"])}
            {row("4 · VAT reclaimed on purchases", b["4_vat_input_reclaimed"])}
            {row("5 · Net VAT owed to HMRC (3-4)", b["5_net_vat_owed_to_hmrc"])}
            {row("6 · Total net sales (ex VAT)", b["6_total_net_sales_ex_vat"])}
            {row("7 · Total net purchases (ex VAT)", b["7_total_net_purchases_ex_vat"])}
            {row("8 · EC supplies of goods", b["8_ec_supplies_of_goods"])}
            {row("9 · EC acquisitions of goods", b["9_ec_acquisitions_of_goods"])}
          </TableBody>
        </Table>
      </CardContent></Card>
  );
};

// ─────────────────────────────────────────────────────────────
// Health Score tab
// ─────────────────────────────────────────────────────────────
function HealthScoreTab() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/accounting/reports/health-score`, authHeaders());
        setH(data);
      } catch (e) { toast.error("Failed to load health score"); }
      setLoading(false);
    })();
  }, []);

  if (loading || !h) return <div>Loading…</div>;
  const scoreColor = h.score >= 70 ? "text-green-600" : h.score >= 40 ? "text-amber-600" : "text-red-600";

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card className="md:col-span-3" data-testid="health-score-main">
        <CardHeader><CardTitle>Financial Health Score</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-4">
            <div className={`text-6xl font-bold font-mono ${scoreColor}`}>{h.score}</div>
            <div className="text-lg text-neutral-500">/ 100</div>
          </div>
          <div className="mt-4 text-sm text-neutral-500">Weighted: cash runway 40% · gross margin 40% · retention 20%</div>
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle>Cash Runway</CardTitle></CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold font-mono">{h.components.cash_runway_months} mo</div>
          <div className="text-xs text-neutral-500 mt-2">Cash: {gbp(h.inputs.cash_gbp)} · Monthly burn: {gbp(h.inputs.monthly_burn)}</div>
          <div className="text-xs text-neutral-500 mt-1">Sub-score: {h.sub_scores.runway}/100</div>
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Gross Margin</CardTitle></CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold font-mono">{h.components.gross_margin_pct}%</div>
          <div className="text-xs text-neutral-500 mt-2">Trailing 3-month period</div>
          <div className="text-xs text-neutral-500 mt-1">Sub-score: {h.sub_scores.margin}/100</div>
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Retention</CardTitle></CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold font-mono">{h.components.active_subscriptions}</div>
          <div className="text-xs text-neutral-500 mt-2">Active paid subscriptions</div>
          <div className="text-xs text-neutral-500 mt-1">Sub-score: {h.sub_scores.retention}/100</div>
        </CardContent></Card>
      <CashflowForecastCard />
    </div>
  );
}

function CashflowForecastCard() {
  const [f, setF] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/accounting/reports/cashflow-forecast?days=${days}`, authHeaders());
        setF(data);
      } catch (e) { /* silent */ }
    })();
  }, [days]);
  if (!f) return null;
  const cashColor = f.ending_cash >= 0 ? "text-green-600" : "text-red-600";
  const points = f.projection || [];
  const min = Math.min(...points.map(p => p.cash), 0);
  const max = Math.max(...points.map(p => p.cash), 1);
  const span = Math.max(1, max - min);
  return (
    <Card className="md:col-span-3" data-testid="cashflow-forecast-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{days}-Day Cashflow Forecast</CardTitle>
        <div className="flex gap-1 border rounded-md p-1 text-sm">
          {[30, 60, 90, 180].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2 py-0.5 rounded ${days === d ? "bg-neutral-900 text-white" : "text-neutral-500"}`}
              data-testid={`forecast-days-${d}`}>{d}d</button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
          <div><div className="text-neutral-500">Starting cash</div><div className="font-mono font-semibold">{gbp(f.starting_cash)}</div></div>
          <div><div className="text-neutral-500">Avg daily net</div><div className="font-mono font-semibold">{gbp(f.net_daily)}</div></div>
          <div><div className="text-neutral-500">Scheduled renewals</div><div className="font-mono font-semibold">{gbp(f.total_scheduled_renewals)}</div></div>
          <div><div className="text-neutral-500">Projected ending cash</div><div className={`font-mono font-semibold ${cashColor}`}>{gbp(f.ending_cash)}</div></div>
        </div>
        {f.runway_exhausted_on && (
          <div className="mb-3 text-sm text-red-600">⚠ Cash exhausted on {f.runway_exhausted_on} at current burn rate</div>
        )}
        {/* Tiny SVG sparkline */}
        <svg viewBox="0 0 800 100" className="w-full h-24 border rounded-md bg-neutral-50">
          <polyline
            fill="none" stroke={f.ending_cash >= 0 ? "#16a34a" : "#dc2626"} strokeWidth="1.5"
            points={points.map((p, i) => `${(i / Math.max(1, points.length - 1)) * 800},${100 - ((p.cash - min) / span) * 90 - 5}`).join(" ")}
          />
          <line x1="0" y1={100 - ((0 - min) / span) * 90 - 5} x2="800" y2={100 - ((0 - min) / span) * 90 - 5} stroke="#999" strokeDasharray="3 3" strokeWidth="0.5" />
        </svg>
        <div className="mt-1 flex justify-between text-xs text-neutral-500">
          <span>{points[0]?.date}</span><span>{points[points.length - 1]?.date}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ImportXeroDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/accounting/import/xero`, { csv_text: csv }, authHeaders());
      if (data.error) { toast.error(`Import failed: ${data.error}`); }
      else { toast.success(`Imported ${data.imported} journals (${data.skipped} skipped)`); setOpen(false); setCsv(""); onSaved(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="import-xero-btn">Import Xero CSV</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Import Xero General Ledger CSV</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-500">Paste the CSV contents. Expected columns: Date, Description, Account Code, Debit, Credit, Journal Ref (all tolerant of common variants).</p>
        <textarea
          className="w-full h-64 font-mono text-xs border rounded-md p-2"
          placeholder="Date,Description,Account Code,Debit,Credit,Journal Ref&#10;2025-01-15,&quot;Stripe payment&quot;,1200,100.00,0.00,J001&#10;2025-01-15,&quot;Repair rev&quot;,4000,0.00,83.33,J001&#10;2025-01-15,&quot;VAT output&quot;,2400,0.00,16.67,J001"
          value={csv} onChange={e => setCsv(e.target.value)}
          data-testid="xero-csv-textarea"
        />
        <div className="flex justify-end"><Button onClick={submit} disabled={busy || csv.length < 10} data-testid="xero-import-submit">
          {busy ? "Importing…" : "Import"}
        </Button></div>
      </DialogContent>
    </Dialog>
  );
}

function PayrollDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([{ name: "", gross_gbp: 0, paye_gbp: 0, employee_nic_gbp: 0, employer_nic_gbp: 0, employee_pension_gbp: 0, employer_pension_gbp: 0, net_pay_gbp: 0 }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const totals = rows.reduce((acc, r) => ({
    gross: acc.gross + Number(r.gross_gbp || 0),
    net:   acc.net   + Number(r.net_pay_gbp || 0),
  }), { gross: 0, net: 0 });
  const submit = async () => {
    setBusy(true);
    try {
      const cleaned = rows.filter(r => r.name && Number(r.gross_gbp) > 0).map(r => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, k === "name" ? v : Number(v || 0)])));
      if (!cleaned.length) { toast.error("Add at least one employee with gross > 0"); setBusy(false); return; }
      const { data } = await axios.post(`${API}/api/accounting/payroll/post-month`, { period, employees: cleaned }, authHeaders());
      if (data.already_posted) toast.info(`Already posted for ${period}`);
      else toast.success(`Payroll posted for ${period}`);
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post payroll"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="payroll-btn">Post Payroll</Button></DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>Post Monthly Payroll</DialogTitle></DialogHeader>
        <div className="flex items-end gap-3">
          <div><Label>Period (YYYY-MM)</Label><Input value={period} onChange={e => setPeriod(e.target.value)} data-testid="payroll-period-input" /></div>
          <Button variant="outline" size="sm" onClick={() => setRows([...rows, { name: "", gross_gbp: 0, paye_gbp: 0, employee_nic_gbp: 0, employer_nic_gbp: 0, employee_pension_gbp: 0, employer_pension_gbp: 0, net_pay_gbp: 0 }])} data-testid="add-employee-btn">+ Add employee</Button>
        </div>
        <div className="max-h-96 overflow-auto border rounded-md">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="min-w-[140px]">Name</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">PAYE</TableHead>
              <TableHead className="text-right">EE NIC</TableHead>
              <TableHead className="text-right">ER NIC</TableHead>
              <TableHead className="text-right">EE Pen</TableHead>
              <TableHead className="text-right">ER Pen</TableHead>
              <TableHead className="text-right">Net Pay</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell><Input value={r.name} onChange={e => set(i, { name: e.target.value })} data-testid={`payroll-name-${i}`} /></TableCell>
                  {["gross_gbp","paye_gbp","employee_nic_gbp","employer_nic_gbp","employee_pension_gbp","employer_pension_gbp","net_pay_gbp"].map(f => (
                    <TableCell key={f}><Input type="number" step="0.01" className="text-right w-24" value={r[f]} onChange={e => set(i, { [f]: e.target.value })} /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-between items-center">
          <div className="font-mono text-sm">Total gross: <b>{gbp(totals.gross)}</b> · Total net: <b>{gbp(totals.net)}</b></div>
          <Button onClick={submit} disabled={busy} data-testid="payroll-submit">{busy ? "Posting…" : "Post payroll"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────
export default function AdminAccounting() {
  return (
    <div className="p-6 space-y-4" data-testid="admin-accounting-page">
      <div>
        <h1 className="text-2xl font-semibold">Accounting</h1>
        <p className="text-sm text-neutral-500">Double-entry ledger · UK Ltd chart of accounts · auto-posted from Stripe + manual journals</p>
      </div>
      <Tabs defaultValue="reports">
        <TabsList data-testid="accounting-main-tabs">
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="journals" data-testid="tab-journals">Journals</TabsTrigger>
          <TabsTrigger value="coa" data-testid="tab-coa">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">Health Score</TabsTrigger>
        </TabsList>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
        <TabsContent value="journals"><JournalsTab /></TabsContent>
        <TabsContent value="coa"><ChartOfAccountsTab /></TabsContent>
        <TabsContent value="health"><HealthScoreTab /></TabsContent>
      </Tabs>
    </div>
  );
}
